import {
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
  LocalStateError,
  ServerError,
  ServerParseError,
  UnconventionalError,
} from '@apollo/client';

/**
 * Returns true if the given value is an Apollo Client error.
 *
 * Apollo Client v4 removed the `ApolloError` base class in favour of specific
 * error types. Use this guard when you need to distinguish Apollo-originated
 * errors from domain errors without caring about the exact subtype. When the
 * subtype matters, use the individual `.is()` guards instead:
 * `CombinedGraphQLErrors.is(error)`, `ServerError.is(error)`, etc.
 *
 * @see https://www.apollographql.com/docs/react/data/error-handling#network-errors
 */
export function isApolloError(error: unknown): error is Error {
  return (
    CombinedGraphQLErrors.is(error) ||
    CombinedProtocolErrors.is(error) ||
    LocalStateError.is(error) ||
    ServerError.is(error) ||
    ServerParseError.is(error) ||
    UnconventionalError.is(error)
  );
}

/**
 * Returns true if the given {@link CombinedGraphQLErrors} contains the
 * specified GraphQL extension code.
 *
 * Use this when the error originates from the correct Apollo v4 path — i.e.
 * when the upstream link throws `new CombinedGraphQLErrors(response)`.
 */
export function hasGraphQLErrorCode(error: unknown, code: string): boolean {
  if (!CombinedGraphQLErrors.is(error)) return false;
  return error.errors.some((e) => e.extensions?.['code'] === code);
}

/**
 * Returns true if the given value is a {@link CombinedGraphQLErrors} instance
 * that carries at least one GraphQL error.
 *
 * Apollo Client v4 reports GraphQL errors via `CombinedGraphQLErrors`, exposed
 * on the mutation/query result `error` property. The empty-array case is
 * treated as "no error" because Apollo never produces such an instance, but the
 * explicit length check keeps consumers safe from any future behaviour change.
 *
 * Acts as a TypeScript type guard so callers can access `error.errors` without
 * an additional `CombinedGraphQLErrors.is` narrow.
 */
export function hasCombinedGraphQLErrors(
  error: unknown,
): error is CombinedGraphQLErrors {
  return CombinedGraphQLErrors.is(error) && error.errors.length > 0;
}
